import { LoaderCircle } from "lucide-react";
import { startTransition } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ProjectForm } from "@/components/projects/project-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { CreateProjectInput } from "@/features/projects";
import { useProjectShellStore } from "@/stores/projectShellStore";

type CreateProjectDialogProps = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

export function CreateProjectDialog({
  onOpenChange,
  open,
}: CreateProjectDialogProps) {
  const { t } = useTranslation();
  const busy = useProjectShellStore((state) => state.createDialogBusy);
  const createProject = useProjectShellStore((state) => state.createProject);
  const navigate = useNavigate();

  async function handleSubmit(input: CreateProjectInput) {
    const project = await createProject(input);
    onOpenChange(false);
    startTransition(() => {
      navigate(`/projects/${project.projectId}`);
    });
  }

  return (
    <Dialog
      onOpenChange={(nextOpen) => {
        if (!busy) {
          onOpenChange(nextOpen)
        }
      }}
      open={open}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("projects.portfolio.createAction")}</DialogTitle>
          <DialogDescription>
            {t("projects.portfolio.createDescription")}
          </DialogDescription>
        </DialogHeader>
        {busy ? (
          <div className="flex items-center gap-3 rounded-[24px] bg-secondary px-4 py-3 text-sm text-secondary-foreground">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            {t("projects.portfolio.creating")}
          </div>
        ) : null}
        <ProjectForm
          busy={busy}
          onSubmit={handleSubmit}
          submitLabel={t("projects.form.create")}
        />
      </DialogContent>
    </Dialog>
  );
}
